import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, CheckCircle, CreditCard, Lock, Zap, Dumbbell, BookOpen, User } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const NIVEL_LABELS = { beginner: "Beginner", gevorderd: "Gevorderd", advanced: "Advanced" };
const DOEL_LABELS = { vetverlies: "Vetverlies", spieropbouw: "Spieropbouw", onderhoud: "Onderhoud", powerlift: "Powerlift", hyrox: "Hyrox" };
const NIVEL_PRIJS = { beginner: 19, gevorderd: 29, advanced: 39 };
const FREQ_TOESLAG = { "2x": 0, "3x": 3, "4x": 5 };

// Persoonlijk schema frequentie prijzen
const PERSOONLIJK_FREQ_PRIJZEN = { "2x": 30, "3x": 40, "4x": 50, "5x": 60 };

function getSchemaInfo(schemaKey) {
  const parts = (schemaKey || "").toLowerCase().split("-");
  const doel = parts.find(p => DOEL_LABELS[p]) || "vetverlies";
  const nivel = parts.find(p => NIVEL_PRIJS[p]) || "beginner";
  const freq = parts.find(p => FREQ_TOESLAG[p]) || "2x";
  const prijs = NIVEL_PRIJS[nivel] + FREQ_TOESLAG[freq];
  return {
    naam: `${DOEL_LABELS[doel]} ${NIVEL_LABELS[nivel]} · ${freq} per week`,
    prijs: `€${prijs},00`,
    prijs_num: prijs,
    voordelen: [
      `Doel: ${DOEL_LABELS[doel]}`,
      `Niveau: ${NIVEL_LABELS[nivel]}`,
      `${freq} per week trainingsschema`,
      "Direct toegankelijk in jouw dashboard",
    ],
  };
}

const VASTE_PRODUCTEN = {
  persoonlijk_schema: (dagen, prijs) => {
    return {
      naam: `Persoonlijk Trainingsschema — ${dagen} dagen per week`,
      prijs: `€${Number(prijs).toFixed(2).replace(".", ",")}`,
      prijs_num: Number(prijs),
      icon: <User size={20} />,
      beschrijving: `Volledig persoonlijk samengesteld schema voor ${dagen} trainingsdagen per week, opgebouwd volgens de FitSmith-methode op basis van jouw intake.`,
      voordelen: [
        `${dagen} trainingsdagen per week`,
        "Volledig gepersonaliseerd via uitgebreide intake",
        "Samengesteld volgens de FitSmith-methode",
        "Direct beschikbaar in jouw account",
      ],
    };
  },
  cursus: {
    naam: "FitSmith PT Kickstart Program",
    prijs: "€399,95",
    prijs_num: 399.95,
    icon: <BookOpen size={20} />,
    beschrijving: "De volledige PT-cursus met 20 lessen, quizzen, eindtoets en alle 36 schema's.",
    voordelen: [
      "20 professionele lessen",
      "Alle 36 trainingsschema's inbegrepen",
      "Quizzen & eindtoets (certificaat)",
      "Levenslange toegang",
    ],
  },
};

export default function BestellenPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const productKey = params.get("product") || "persoonlijk_schema";
  const schemaKey = params.get("schema") || "";
  const freq = params.get("freq") || "3x";

  // PT: dagen-keuze + prijzen uit beheer
  const [ptDagen, setPtDagen] = useState(String(parseInt(freq) || 3));
  const [ptPrijzen, setPtPrijzen] = useState({ "2": 9.95, "3": 14.95, "4": 19.95, "5": 24.95, "6": 29.95 });
  useEffect(() => {
    if (productKey !== "persoonlijk_schema") return;
    fetch(`${API}/api/pt/prijzen`).then(r => r.json()).then(d => d.prijzen && setPtPrijzen(d.prijzen)).catch(() => {});
  }, [productKey]);

  // Haal productinfo op
  let product;
  if (productKey === "standaard_schema" && schemaKey) {
    const info = getSchemaInfo(schemaKey);
    product = { ...info, icon: <Dumbbell size={20} />, beschrijving: "Direct beschikbaar in jouw account na betaling." };
  } else if (productKey === "persoonlijk_schema") {
    product = VASTE_PRODUCTEN.persoonlijk_schema(ptDagen, ptPrijzen[ptDagen] ?? 14.95);
  } else {
    const p = VASTE_PRODUCTEN[productKey];
    product = typeof p === "function" ? p(ptDagen, ptPrijzen[ptDagen] ?? 14.95) : p || VASTE_PRODUCTEN.persoonlijk_schema(ptDagen, ptPrijzen[ptDagen] ?? 14.95);
  }

  const [form, setForm] = useState({ naam: "", email: "", akkoord: false, restitutie: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Kortingscode
  const [kortingscode, setKortingscode] = useState("");
  const [kortingValideren, setKortingValideren] = useState(false);
  const [kortingPercentage, setKortingPercentage] = useState(0);
  const [kortingFout, setKortingFout] = useState("");

  const valideerKorting = async () => {
    if (!kortingscode.trim()) return;
    setKortingValideren(true); setKortingFout(""); setKortingPercentage(0);
    try {
      const r = await fetch(`${API}/api/discount/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: kortingscode.trim() }),
      });
      let d;
      try { d = await r.json(); } catch { d = {}; }
      if (!r.ok) throw new Error(d.detail || "Ongeldige kortingscode");
      setKortingPercentage(d.percentage);
    } catch (err) {
      setKortingFout(err.message || "Ongeldige kortingscode");
    }
    finally { setKortingValideren(false); }
  };

  const validate = () => {
    const e = {};
    if (!form.naam.trim()) e.naam = "Naam is verplicht";
    if (!form.email.trim() || !form.email.includes("@")) e.email = "Geldig e-mailadres verplicht";
    if (!form.akkoord) e.akkoord = "Akkoord verplicht";
    if (!form.restitutie) e.restitutie = "Je moet akkoord gaan met het restitutiebeleid";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      // Persoonlijk schema: eerst intake, daarna betalen
      if (productKey === "persoonlijk_schema") {
        const res = await fetch(`${API}/api/pt/start`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ naam: form.naam, email: form.email, dagen: ptDagen }),
        });
        const data = await res.json();
        if (data.order_id) {
          const kortingParam = kortingscode.trim() && kortingPercentage > 0 ? `?korting=${encodeURIComponent(kortingscode.trim().toUpperCase())}` : "";
          navigate(`/pt-intake/${data.order_id}${kortingParam}`);
        } else {
          setErrors({ submit: data.detail || "Er ging iets mis, probeer opnieuw." });
          setSubmitting(false);
        }
        return;
      }

      const body = { naam: form.naam, email: form.email, product: productKey };
      if (productKey === "standaard_schema") body.schema_key = schemaKey;
      if (productKey === "persoonlijk_schema") body.freq = freq;
      if (kortingscode.trim() && kortingPercentage > 0) body.kortingscode = kortingscode.trim().toUpperCase();

      const res = await fetch(`${API}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setErrors({ submit: data.detail || "Er ging iets mis, probeer opnieuw." });
        setSubmitting(false);
      }
    } catch {
      setErrors({ submit: "Er ging iets mis, probeer opnieuw." });
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080808]">
      {/* Header */}
      <div className="border-b border-white/5 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            className="w-8 h-8 rounded-xl bg-[#141414] border border-white/6 flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft size={16} />
          </button>
          <span className="text-zinc-400 text-sm">Bestelling plaatsen</span>
          <div className="ml-auto flex items-center gap-1.5 text-zinc-600 text-xs">
            <Lock size={11} /> Beveiligde betaling via Mollie
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Formulier */}
        <div className="lg:col-span-3">
          <h1 className="text-white font-bold text-2xl mb-1">Jouw gegevens</h1>
          <p className="text-zinc-500 text-sm mb-8">
            {productKey === "persoonlijk_schema"
              ? "Je vult eerst een korte intake in over jouw doelen en situatie, daarna reken je veilig af."
              : productKey === "cursus"
              ? "Na betaling krijg je direct toegang tot de cursusomgeving."
              : "Na betaling staat het schema direct klaar in jouw account."}
          </p>

          {productKey === "persoonlijk_schema" && (
            <div className="mb-8">
              <p className="text-zinc-400 text-xs font-bold uppercase tracking-widest mb-3">Trainingsdagen per week</p>
              <div className="flex flex-wrap gap-2" data-testid="pt-dagen-keuze">
                {["2", "3", "4", "5", "6"].map(d => (
                  <button key={d} type="button" onClick={() => setPtDagen(d)} data-testid={`pt-dagen-${d}`}
                    className={`px-4 py-3 rounded-xl text-sm font-bold border transition-all flex flex-col items-center min-w-[76px] ${ptDagen === d ? "text-black border-transparent" : "bg-[#111] text-zinc-400 border-white/10 hover:border-white/25"}`}
                    style={ptDagen === d ? { background: "#C9A84C" } : {}}>
                    <span>{d} dagen</span>
                    <span className={`text-xs font-semibold ${ptDagen === d ? "text-black/70" : "text-zinc-600"}`}>
                      €{Number(ptPrijzen[d] ?? 0).toFixed(2).replace(".", ",")}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <label className="flex flex-col gap-2">
              <span className="text-zinc-400 text-xs font-bold uppercase tracking-widest">Volledige naam</span>
              <input type="text" value={form.naam}
                onChange={e => setForm(f => ({ ...f, naam: e.target.value }))}
                placeholder="bijv. Jan de Vries" data-testid="bestel-naam"
                className="bg-[#111] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-[#C9A84C]/50 transition-colors" />
              {errors.naam && <p className="text-red-400 text-xs">{errors.naam}</p>}
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-zinc-400 text-xs font-bold uppercase tracking-widest">E-mailadres</span>
              <input type="email" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="jij@voorbeeld.nl" data-testid="bestel-email"
                className="bg-[#111] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-[#C9A84C]/50 transition-colors" />
              {errors.email && <p className="text-red-400 text-xs">{errors.email}</p>}
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.akkoord}
                onChange={e => setForm(f => ({ ...f, akkoord: e.target.checked }))}
                data-testid="bestel-akkoord" className="mt-1 accent-[#C9A84C]" />
              <span className="text-zinc-400 text-sm leading-relaxed">
                Ik ga akkoord met de{" "}
                <a href="/algemene-voorwaarden" target="_blank" rel="noopener noreferrer"
                  className="text-[#C9A84C] hover:underline font-semibold">algemene voorwaarden</a>
                {" "}en het{" "}
                <a href="/privacybeleid" target="_blank" rel="noopener noreferrer"
                  className="text-[#C9A84C] hover:underline font-semibold">privacybeleid</a>
                {" "}van FitSmith.
              </span>
            </label>
            {errors.akkoord && <p className="text-red-400 text-xs -mt-3">{errors.akkoord}</p>}

            {/* Restitutiebeleid checkbox — verplicht */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.restitutie}
                onChange={e => setForm(f => ({ ...f, restitutie: e.target.checked }))}
                data-testid="bestel-restitutie" className="mt-1 accent-[#C9A84C]" />
              <span className="text-zinc-400 text-sm leading-relaxed">
                Ik ga akkoord met het{" "}
                <a href="/restitutiebeleid" target="_blank" rel="noopener noreferrer"
                  className="text-[#C9A84C] hover:underline font-semibold">
                  restitutiebeleid
                </a>{" "}
                en zie af van mijn 14 dagen bedenktijd omdat ik direct toegang krijg tot het digitale product.
              </span>
            </label>
            {errors.restitutie && <p className="text-red-400 text-xs -mt-3">{errors.restitutie}</p>}

            {/* Kortingscode */}
            <div className="border-t border-white/5 pt-5">
              <p className="text-zinc-400 text-xs font-bold uppercase tracking-widest mb-2">Kortingscode</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={kortingscode}
                  onChange={e => { setKortingscode(e.target.value.toUpperCase()); setKortingPercentage(0); setKortingFout(""); }}
                  placeholder="ZOMER25"
                  data-testid="kortingscode-input"
                  className="flex-1 bg-[#111] border border-white/10 rounded-xl px-4 py-3 text-white font-mono text-sm placeholder-zinc-600 focus:outline-none focus:border-[#C9A84C]/50 uppercase"
                />
                <button
                  type="button"
                  onClick={valideerKorting}
                  disabled={kortingValideren || !kortingscode.trim()}
                  data-testid="kortingscode-toepassen-btn"
                  className="px-5 py-3 rounded-xl font-bold text-sm border border-white/10 text-zinc-400 hover:border-[#C9A84C]/40 hover:text-[#C9A84C] disabled:opacity-40 transition-all whitespace-nowrap"
                >
                  {kortingValideren ? "..." : "Toepassen"}
                </button>
              </div>
              {kortingPercentage > 0 && (
                <div className="mt-2 flex items-center gap-2 text-sm text-green-400 font-semibold">
                  <CheckCircle size={14} /> {kortingPercentage}% korting toegepast!
                </div>
              )}
              {kortingFout && <p className="mt-2 text-red-400 text-sm">{kortingFout}</p>}
            </div>

            {errors.submit && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                <p className="text-red-400 text-sm">{errors.submit}</p>
              </div>
            )}

            <button type="submit" disabled={submitting} data-testid="bestel-submit"
              className="w-full py-4 rounded-xl font-bold text-sm uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
              style={{ background: submitting ? "#3f3f46" : "#C9A84C", color: "#000" }}>
              {submitting ? (
                <><div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />{productKey === "persoonlijk_schema" ? "Naar de intake..." : "Doorsturen naar betaalpagina..."}</>
              ) : kortingPercentage > 0 ? (
                <><CreditCard size={16} />{productKey === "persoonlijk_schema" ? "Start intake" : "Betalen"} — €{(product.prijs_num * (1 - kortingPercentage / 100)).toFixed(2).replace(".", ",")} <span className="line-through text-black/40 font-normal normal-case tracking-normal">{product.prijs}</span></>
              ) : (
                <><CreditCard size={16} />{productKey === "persoonlijk_schema" ? "Start intake" : "Betalen"} — {product.prijs}</>
              )}
            </button>
          </form>

          <div className="mt-8 flex flex-wrap gap-4">
            {[
              { icon: <Shield size={13} />, label: "SSL beveiligd" },
              { icon: <Lock size={13} />, label: "iDEAL / Creditcard" },
              { icon: <Zap size={13} />, label: "Direct toegang na betaling" },
            ].map(b => (
              <div key={b.label} className="flex items-center gap-1.5 text-zinc-600 text-xs">{b.icon} {b.label}</div>
            ))}
          </div>
        </div>

        {/* Order samenvatting */}
        <div className="lg:col-span-2">
          <div className="bg-[#111] border border-white/8 rounded-2xl p-6 sticky top-6">
            <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-4">Jouw bestelling</p>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center text-[#C9A84C]">
                {product.icon}
              </div>
              <h2 className="text-white font-bold text-base leading-tight">{product.naam}</h2>
            </div>
            <p className="text-zinc-500 text-sm mb-5">{product.beschrijving}</p>

            <div className="space-y-2 mb-5">
              {product.voordelen.map((v, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-zinc-400">
                  <CheckCircle size={14} className="text-[#C9A84C] mt-0.5 shrink-0" />
                  {v}
                </div>
              ))}
            </div>

            <div className="border-t border-white/6 pt-4 flex items-center justify-between">
              <span className="text-zinc-500 text-sm">Totaal</span>
              <span className="text-white font-black text-2xl">{product.prijs}</span>
            </div>
            <p className="text-zinc-700 text-xs mt-1 text-right">Incl. BTW · Eenmalige betaling</p>
          </div>
        </div>
      </div>
    </div>
  );
}
